import { loadAssets } from '@/app/gridGL/loadAssets';
import { startupTimer } from '@/app/gridGL/helpers/startupTimer';
import initCoreClient from '@/app/quadratic-core/quadratic_core';
import { initCoreWorkers, initLanguageWorkers } from '@/app/web-workers/workers';
import { atom, getDefaultStore } from 'jotai';

export interface PreloadState {
  wasmReady: boolean;
  coreWorkersReady: boolean;
  languageWorkersReady: boolean;
  assetsReady: boolean;
  emptyGridReady: boolean;
}

export interface PreloadOptions {
  preloadPython?: boolean;
  preloadJavascript?: boolean;
}

export const preloadStateAtom = atom<PreloadState>({
  wasmReady: false,
  coreWorkersReady: false,
  languageWorkersReady: false,
  assetsReady: false,
  emptyGridReady: false,
});

let preloadStarted = false;
let preloadPromise: Promise<void> | null = null;
// Logged once per process. Browser-induced preload aborts (Memory Saver / tab
// discard / network drop) cause every parallel sub-promise to reject in the
// same microtask burst, and each call site of `preloadCore` would otherwise
// re-emit "[Preload] Failed to preload core" — producing 10+ duplicate
// console / Sentry breadcrumbs for a single underlying failure.
let preloadFailureLogged = false;

/**
 * Preloads WASM, core workers, assets, and optionally language workers.
 * Safe to call multiple times - only runs once.
 */
export const preloadCore = async (options: PreloadOptions = {}): Promise<void> => {
  const { preloadPython = true, preloadJavascript = true } = options;

  try {
    if (preloadStarted) {
      // If already started, wait for existing preload to complete
      if (preloadPromise) await preloadPromise;
      // Then ensure language workers are loaded if requested
      if (preloadPython || preloadJavascript) {
        await preloadLanguageWorkers({ python: preloadPython, javascript: preloadJavascript });
      }
      return;
    }

    preloadStarted = true;
    const store = getDefaultStore();
    startupTimer.start('preload');

    preloadPromise = doPreload(store, { preloadPython, preloadJavascript });
    await preloadPromise;
    startupTimer.end('preload');
  } catch (error) {
    if (!preloadFailureLogged) {
      preloadFailureLogged = true;
      console.warn('[Preload] Failed to preload core:', error);
    }
    throw error;
  }
};

const doPreload = async (
  store: ReturnType<typeof getDefaultStore>,
  { preloadPython, preloadJavascript }: { preloadPython: boolean; preloadJavascript: boolean }
): Promise<void> => {
  const wasmPromise = (async () => {
    startupTimer.start('preload.wasm');
    await initCoreClient();
    startupTimer.end('preload.wasm');
    store.set(preloadStateAtom, (prev) => ({ ...prev, wasmReady: true }));
  })();

  const coreWorkersPromise = (async () => {
    startupTimer.start('preload.coreWorkers');
    initCoreWorkers();
    startupTimer.end('preload.coreWorkers');
    store.set(preloadStateAtom, (prev) => ({ ...prev, coreWorkersReady: true }));
  })();

  const assetsPromise = (async () => {
    startupTimer.start('preload.assets');
    await loadAssets();
    startupTimer.end('preload.assets');
    store.set(preloadStateAtom, (prev) => ({ ...prev, assetsReady: true }));
  })();

  const langWorkersPromise = (async () => {
    if (preloadPython || preloadJavascript) {
      startupTimer.start('preload.langWorkers');
      initLanguageWorkers({ python: preloadPython, javascript: preloadJavascript });
      startupTimer.end('preload.langWorkers');
      store.set(preloadStateAtom, (prev) => ({ ...prev, languageWorkersReady: true }));
    }
  })();

  const emptyGridPromise = (async () => {
    // Wait for both WASM and assets since initEmptyGrid depends on both
    await Promise.all([wasmPromise, assetsPromise]);
    startupTimer.start('preload.emptyGrid');
    await initEmptyGridInternal(store);
    startupTimer.end('preload.emptyGrid');
  })();

  // Use `allSettled` so that when one sub-promise rejects (typically the WASM
  // fetch being aborted by the browser) the others don't surface as
  // "unhandled promise rejection" breadcrumbs in Sentry. We still throw the
  // first rejection so callers like `embed.tsx` that `await preloadCore` see
  // a proper failure.
  const results = await Promise.allSettled([
    wasmPromise,
    coreWorkersPromise,
    assetsPromise,
    langWorkersPromise,
    emptyGridPromise,
  ]);
  const firstRejection = results.find((r): r is PromiseRejectedResult => r.status === 'rejected');
  if (firstRejection) {
    throw firstRejection.reason;
  }
};

/**
 * Preloads language workers (Python/JavaScript) on demand.
 * Safe to call multiple times.
 */
export const preloadLanguageWorkers = async (
  options: { python?: boolean; javascript?: boolean } = {}
): Promise<void> => {
  const { python = true, javascript = true } = options;
  const store = getDefaultStore();
  const state = store.get(preloadStateAtom);

  if (state.languageWorkersReady) return;

  initLanguageWorkers({ python, javascript });
  store.set(preloadStateAtom, (prev) => ({ ...prev, languageWorkersReady: true }));
};

/**
 * Returns a promise that resolves when core preloading is complete.
 * Useful for waiting on preload before proceeding with file loading.
 */
export const waitForPreload = async (): Promise<void> => {
  if (preloadPromise) {
    await preloadPromise;
  }
};

/**
 * Internal helper to initialize empty grid.
 * Takes store as parameter to avoid repeated getDefaultStore calls during preload.
 */
const initEmptyGridInternal = async (store: ReturnType<typeof getDefaultStore>): Promise<void> => {
  const state = store.get(preloadStateAtom);
  if (state.emptyGridReady) return;

  // Lazy import to avoid circular dependency issues with Content.ts/Cursor.ts
  const { pixiApp } = await import('@/app/gridGL/pixiApp/PixiApp');
  await pixiApp.initEmpty();
  store.set(preloadStateAtom, (prev) => ({ ...prev, emptyGridReady: true }));
};

/**
 * Initializes an empty grid state (headers without cells).
 * Call this after assets are loaded to prepare the grid for display.
 */
export const initEmptyGrid = async (): Promise<void> => {
  const store = getDefaultStore();
  const state = store.get(preloadStateAtom);

  if (state.emptyGridReady) return;

  if (!state.assetsReady) {
    await loadAssets();
    store.set(preloadStateAtom, (prev) => ({ ...prev, assetsReady: true }));
  }

  await initEmptyGridInternal(store);
};

export const isCorePreloaded = (): boolean => {
  const state = getDefaultStore().get(preloadStateAtom);
  return state.wasmReady && state.coreWorkersReady && state.assetsReady;
};

export const isFullyPreloaded = (): boolean => {
  const state = getDefaultStore().get(preloadStateAtom);
  return state.wasmReady && state.coreWorkersReady && state.languageWorkersReady && state.assetsReady;
};

export const getPreloadState = (): PreloadState => {
  return getDefaultStore().get(preloadStateAtom);
};
